import { Component, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { CameraPreferenceService } from '../../../services/camera-preference.service';

export interface BarcodeScannerDialogData {
  title: string;
  allowedFormats: BarcodeFormat[];
}

@Component({
  selector: 'lfe-barcode-scanner-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatIcon, ZXingScannerModule],
  templateUrl: './barcode-scanner-dialog.component.html',
  styleUrl: './barcode-scanner-dialog.component.css',
  encapsulation: ViewEncapsulation.None
})
export class BarcodeScannerDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<BarcodeScannerDialogComponent>);
  private readonly cameraPreferenceService = inject(CameraPreferenceService);
  readonly data: BarcodeScannerDialogData = inject(MAT_DIALOG_DATA);
  readonly translateService = inject(LiveFormsTranslateLoaderService);

  showScanner = signal(false);
  scannedBarcode = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  availableDevices = signal<MediaDeviceInfo[]>([]);
  currentDevice = signal<MediaDeviceInfo | undefined>(undefined);

  constructor() {
    this.requestCameraAccess();
  }

  ngOnDestroy(): void {
    this.currentDevice.set(undefined);
  }

  async requestCameraAccess(): Promise<void> {
    try {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let videoDevices = devices.filter(device => device.kind === 'videoinput');

      if (videoDevices.length > 0 && !videoDevices[0].label) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());

        devices = await navigator.mediaDevices.enumerateDevices();
        videoDevices = devices.filter(device => device.kind === 'videoinput');
      }

      this.availableDevices.set(videoDevices);

      const preferredCamera = this.cameraPreferenceService.selectPreferredCamera(videoDevices);
      this.currentDevice.set(preferredCamera);
      this.showScanner.set(true);
    } catch (error) {
      console.error('Camera access error:', error);
      this.errorMessage.set(
        this.translateService.instant('liveforms-editor.barcode-item.scanner.error')
      );
    }
  }

  forceSetCurrentDevice(): void {
    const devices = this.availableDevices();
    if (!devices || !devices.length) {
      return;
    }
    const preferredCamera = this.cameraPreferenceService.selectPreferredCamera(devices);
    if (preferredCamera) {
      this.currentDevice.set(preferredCamera);
    }
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableDevices.set(devices);
  }

  onCodeResult(resultString: string): void {
    this.scannedBarcode.set(resultString);
    this.showScanner.set(false);
  }

  onPermissionResponse(hasPermission: boolean): void {
    if (!hasPermission) {
      this.errorMessage.set(
        this.translateService.instant('liveforms-editor.barcode-item.scanner.error')
      );
    }
  }

  onScanError(error: Error): void {
    console.error('Scanner error:', error);
  }

  retake(): void {
    this.scannedBarcode.set(null);
    this.showScanner.set(true);
    this.errorMessage.set(null);
  }

  confirm(): void {
    this.dialogRef.close(this.scannedBarcode());
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  switchCamera(): void {
    const devices = this.availableDevices();
    const current = this.currentDevice();

    if (devices.length <= 1) {
      return;
    }

    const currentIndex = devices.findIndex(device => device.deviceId === current?.deviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    const nextDevice = devices[nextIndex];

    this.currentDevice.set(nextDevice);

    if (nextDevice) {
      this.cameraPreferenceService.savePreferredCamera(nextDevice.deviceId);
    }
  }
}
